
import { useState } from 'react';

const Description = () => {
  const [selectedProject, setSelectedProject] = useState('1');
  const [isEditing, setIsEditing] = useState(false);

  const projects = [
    {
      id: '1',
      name: 'Website Redesign for TechCorp',
      client: 'TechCorp Solutions',
      status: 'In Progress',
      startDate: '2024-01-15',
      endDate: '2024-02-15',
      budget: '$25,000',
      description: 'Complete redesign of the corporate website including new branding, improved user experience, and mobile optimization. The project includes wireframing, design mockups, development, and testing phases.',
      objectives: [
        'Modernize the visual design and branding',
        'Improve mobile responsiveness',
        'Enhance user experience and navigation',
        'Optimize for search engines',
        'Integrate with existing CRM system'
      ],
      deliverables: [
        'Wireframes and user flow diagrams',
        'High-fidelity design mockups',
        'Responsive website development',
        'Content management system',
        'SEO optimization',
        'Testing and quality assurance'
      ]
    },
    {
      id: '2',
      name: 'Mobile App Development',
      client: 'StartupXYZ',
      status: 'In Progress',
      startDate: '2024-01-20',
      endDate: '2024-03-01',
      budget: '$45,000',
      description: 'Development of a cross-platform mobile application for iOS and Android with real-time messaging, user profiles, and payment integration.',
      objectives: [
        'Create intuitive mobile user interface',
        'Implement real-time messaging system',
        'Integrate secure payment processing',
        'Develop user authentication system'
      ],
      deliverables: [
        'Mobile app for iOS and Android',
        'Backend API development',
        'User authentication system',
        'Payment gateway integration',
        'App store deployment'
      ]
    }
  ];

  const currentProject = projects.find(p => p.id === selectedProject);

  return (
    <div className="space-y-6">
      {/* Project Selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <label htmlFor="project-select" className="text-sm font-medium text-gray-700">
            Select Project:
          </label>
          <select
            id="project-select"
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8"
          >
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={() => setIsEditing(!isEditing)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap"
        >
          <i className="ri-edit-line mr-2"></i>
          {isEditing ? 'Save Changes' : 'Edit Project'}
        </button>
      </div>

      {currentProject && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Project Overview */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Overview</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Client</label>
                  <p className="text-gray-900">{currentProject.client}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                    {currentProject.status}
                  </span>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Start Date</label>
                  <p className="text-gray-900">{currentProject.startDate}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">End Date</label>
                  <p className="text-gray-900">{currentProject.endDate}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Budget</label>
                  <p className="text-gray-900 font-semibold">{currentProject.budget}</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Description</h3>
              {isEditing ? (
                <textarea
                  className="w-full h-32 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  defaultValue={currentProject.description}
                />
              ) : (
                <p className="text-gray-700 leading-relaxed">{currentProject.description}</p>
              )}
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Objectives</h3>
              <ul className="space-y-2">
                {currentProject.objectives.map((objective, index) => (
                  <li key={index} className="flex items-start">
                    <i className="ri-check-line text-green-500 mr-2 mt-0.5"></i>
                    <span className="text-gray-700">{objective}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Deliverables */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Deliverables</h3>
            <div className="space-y-3">
              {currentProject.deliverables.map((deliverable, index) => (
                <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-xs font-medium text-blue-600">{index + 1}</span>
                  </div>
                  <span className="text-sm text-gray-700">{deliverable}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Project Progress</h4>
              <div className="flex items-center justify-between text-sm text-blue-700 mb-2">
                <span>Overall Completion</span>
                <span>75%</span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div className="bg-blue-600 h-2 rounded-full" style={{ width: '75%' }}></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Description;
